import { useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Button, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import WbKizSendControls from "./WbKizSendControls";
import { copyKizText, getKizValidationError } from "../../utils/kiz";

export default function KizWritingField({
  it,
  savingId,
  onChangeKiz,
  onSaveKiz,
  posting,
  onSendToWb,
  sendingWbId,
  onAddAdditionalKiz,
  savingAdditionalId,
  onRemoveAdditionalKiz,
}) {
  const [additionalValue, setAdditionalValue] = useState("");
  const busy = savingId === it.documentId;
  const sending = sendingWbId === it.documentId;
  const addingAdditional = savingAdditionalId === it.documentId;
  const canSendToWb =
    (posting?.market || "").toLowerCase() === "wb" && posting?.wbOrderId;
  const additionalCodes = Array.isArray(it.kiz_additional_codes)
    ? it.kiz_additional_codes
    : [];
  const kizValue = String(it.kiz_code || "");
  const kizTrimmed = kizValue.trim();
  const kizErrorMessage = kizTrimmed ? getKizValidationError(kizTrimmed) : "";
  const isKizInvalid = Boolean(kizErrorMessage);
  const additionalErrorMessage = additionalValue.trim()
    ? getKizValidationError(additionalValue)
    : "";

  const handleAddAdditional = async () => {
    const clean = String(additionalValue || "").trim();
    if (!clean || getKizValidationError(clean)) return;
    try {
      await onAddAdditionalKiz?.(it, clean);
      setAdditionalValue("");
    } catch (err) {
      // noop, error handled upstream
    }
  };
  return (
    <>
      <Typography variant="caption" color="text.secondary">
        Ввод КИЗ (сканер вставит код и Enter):
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="(01)0460…(21)…"
          value={kizValue}
          disabled={busy}
          error={isKizInvalid}
          helperText={kizErrorMessage}
          onChange={(e) => onChangeKiz?.(it, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (!isKizInvalid && kizTrimmed) {
                onSaveKiz?.(it);
              }
            }
          }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={() => {
            if (!isKizInvalid && kizTrimmed) {
              onSaveKiz?.(it);
            }
          }}
          disabled={busy || !kizTrimmed || isKizInvalid}
          sx={{ minWidth: 96 }}
        >
          {busy ? "Сохран…" : "Добавить"}
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
        Дополнительные КИЗ (необязательные):
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Доп. КИЗ"
          value={additionalValue}
          disabled={addingAdditional}
          error={Boolean(additionalErrorMessage)}
          helperText={additionalErrorMessage}
          onChange={(e) => setAdditionalValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddAdditional();
            }
          }}
        />
        <IconButton
          color="primary"
          size="small"
          onClick={handleAddAdditional}
          disabled={addingAdditional || !String(additionalValue || "").trim() || Boolean(additionalErrorMessage)}
          sx={{ border: 1, borderColor: "divider" }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Stack>
      {additionalCodes.length > 0 && (
        <Stack spacing={0.75} sx={{ mt: 1 }}>
          {additionalCodes.map((code) => (
            <Stack key={code} direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                fullWidth
                value={code}
                InputProps={{ readOnly: true }}
              />
              <Tooltip title="Скопировать дополнительный КИЗ">
                <IconButton size="small" aria-label="Скопировать дополнительный КИЗ" onClick={() => copyKizText(code)}>
                  <ContentCopyIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              <IconButton
                color="error"
                size="small"
                onClick={() => onRemoveAdditionalKiz?.(it, code)}
                disabled={addingAdditional}
                sx={{ border: 1, borderColor: "divider" }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}
      {canSendToWb && (
        <WbKizSendControls
          busy={sending}
          sent={!!it.kiz_sent}
          disabled={!String(it.kiz_code || "").trim()}
          onSend={() => onSendToWb?.(it)}
        />
      )}
    </>
  );
}
